<template>
  <div class="fmt-tool">
    <div class="fmt-toolbar">
      <span class="fmt-label">{{ $t('tool.JwtDecoder.title') }}</span>
      <span class="fmt-spacer"/>
      <span :class="['uc-chip',{on:mode==='decode'}]" @click="mode='decode'">{{ $t("common.action.decode") }}</span>
      <span :class="['uc-chip',{on:mode==='encode'}]" @click="mode='encode'">{{ $t("common.action.encode") }}</span>
      <el-button size="small" plain icon="el-icon-star-off" @click="loadSample">{{ $t("common.action.sample") }}</el-button>
      <el-button size="small" plain icon="el-icon-document-copy" @click="doCopy">{{ $t("common.action.copy") }}</el-button>
      <el-button size="small" plain icon="el-icon-delete" @click="clearAll">{{ $t("common.action.clear") }}</el-button>
    </div>

    <!-- 解码模式 -->
    <template v-if="mode==='decode'">
      <el-input v-model="token" type="textarea" :rows="3"
        :placeholder="$t('tool.JwtDecoder.jwtInput')"
        class="fmt-textarea jw-short" @input="doDecode"/>
      <div class="fmt-status-bar">
        <span v-if="errorMsg" class="fmt-err"><i class="el-icon-warning"/> {{ errorMsg }}</span>
        <span v-else-if="decoded" class="fmt-ok"><i class="el-icon-circle-check"/> {{ $t('tool.JwtDecoder.decodeOk') }}</span>
        <span v-else class="fmt-hint">{{ $t('tool.JwtDecoder.decodeHint') }}</span>
      </div>
      <template v-if="decoded">
        <div class="jw-panes">
          <div class="jw-pane">
            <div class="jw-pane-title">Header</div>
            <pre class="jw-code">{{ fmtJson(decoded.header) }}</pre>
          </div>
          <div class="jw-pane">
            <div class="jw-pane-title">Payload</div>
            <pre class="jw-code">{{ fmtJson(decoded.payload) }}</pre>
            <div class="jw-claims" v-if="decoded.payload">
              <div class="jw-claim" v-if="decoded.payload.iss"><span>{{ $t('tool.JwtDecoder.claimsIss') }}</span><code>{{ decoded.payload.iss }}</code></div>
              <div class="jw-claim" v-if="decoded.payload.sub"><span>{{ $t('tool.JwtDecoder.claimsSub') }}</span><code>{{ decoded.payload.sub }}</code></div>
              <div class="jw-claim" v-if="decoded.payload.aud"><span>{{ $t('tool.JwtDecoder.claimsAud') }}</span><code>{{ decoded.payload.aud }}</code></div>
              <div class="jw-claim" v-if="decoded.payload.iat"><span>{{ $t('tool.JwtDecoder.claimsIat') }}</span><code>{{ ts2str(decoded.payload.iat) }}</code></div>
              <div class="jw-claim" v-if="decoded.payload.exp">
                <span>{{ $t('tool.JwtDecoder.claimsExp') }}</span><code>{{ ts2str(decoded.payload.exp) }}</code>
                <span v-if="decoded.payload.exp * 1000 < Date.now()" class="jw-badge jw-badge-err">{{ $t('tool.JwtDecoder.statusExpired') }}</span>
                <span v-else class="jw-badge jw-badge-ok">{{ $t('tool.JwtDecoder.statusValid') }}</span>
              </div>
              <div class="jw-claim" v-if="decoded.payload.nbf"><span>{{ $t('tool.JwtDecoder.claimsNbf') }}</span><code>{{ ts2str(decoded.payload.nbf) }}</code></div>
              <div class="jw-claim" v-if="decoded.payload.jti"><span>{{ $t('tool.JwtDecoder.claimsJti') }}</span><code>{{ decoded.payload.jti }}</code></div>
            </div>
          </div>
        </div>
        <div class="jw-pane jw-sig-pane">
          <div class="jw-pane-title">Signature <span class="jw-sig-hint">{{ $t('tool.JwtDecoder.sigHint') }}</span></div>
          <code class="jw-sig">{{ decoded.signature }}</code>
        </div>
        <!-- 签名验证 -->
        <div class="jw-verify">
          <el-input v-model="verifySecret" size="small" :placeholder="$t('tool.JwtDecoder.phVerifySecret')" style="width:300px;" class="jw-mono" @keyup.enter="verifySig"/>
          <el-button size="small" @click="verifySig">{{ $t('tool.JwtDecoder.verify') }}</el-button>
          <span v-if="sigResult==='ok'" class="jw-badge jw-badge-ok">{{ $t('tool.JwtDecoder.verifyOk') }}</span>
          <span v-if="sigResult==='fail'" class="jw-badge jw-badge-err">{{ $t('tool.JwtDecoder.verifyFail') }}</span>
        </div>
      </template>
    </template>

    <!-- 编码模式 -->
    <template v-else>
      <div class="jw-panes">
        <div class="jw-pane">
          <div class="jw-pane-title">Header <span class="jw-sig-hint">{{ $t('tool.JwtDecoder.jsonHint') }}</span></div>
          <el-input v-model="encHeader" type="textarea" :rows="4"
            placeholder='{"alg":"HS256","typ":"JWT"}'
            class="fmt-textarea jw-short"/>
        </div>
        <div class="jw-pane">
          <div class="jw-pane-title">Payload <span class="jw-sig-hint">{{ $t('tool.JwtDecoder.jsonHint') }}</span></div>
          <el-input v-model="encPayload" type="textarea" :rows="4"
            placeholder='{"sub":"123","name":"test","iat":...}'
            class="fmt-textarea jw-short"/>
        </div>
      </div>
      <div class="jw-encode-ctrl">
        <el-select v-model="encAlg" size="small" style="width:130px;">
          <el-option v-for="a in algorithms" :key="a.value" :label="a.label" :value="a.value"/>
        </el-select>
        <el-input v-model="encSecret" size="small" :placeholder="$t('tool.JwtDecoder.phEncSecret')" style="width:280px;" class="jw-mono" type="password" show-password/>
        <el-button type="primary" size="small" @click="doEncode">{{ $t('tool.JwtDecoder.generate') }}</el-button>
      </div>
      <div class="jw-result" v-if="encResult">
        <div class="jw-pane-title">{{ $t('tool.JwtDecoder.generatedJwt') }}</div>
        <code class="jw-result-token">{{ encResult }}</code>
        <el-button size="mini" plain @click="copy(encResult)" style="margin-top:6px;">{{ $t("common.action.copy") }}</el-button>
      </div>
      <div class="fmt-status-bar">
        <span v-if="encErr" class="fmt-err"><i class="el-icon-warning"/> {{ encErr }}</span>
      </div>
    </template>
  </div>
</template>

<script>
var SAMPLE_TOKEN = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkpvaG4gRG9lIiwiaWF0IjoxNTE2MjM5MDIyLCJleHAiOjE3MTYyMzkwMjJ9.4AdrcpX5xv1CF_TFv3HOh4tF6HtRVY4jZpvW98NpXiY'

export default {
  name:'JwtDecoderTool',props:{config:{type:Object,default:()=>({})}},
  data:function(){
    return {
      mode:'decode',token:'',decoded:null,errorMsg:'',
      verifySecret:'',sigResult:'',
      encHeader:'{"alg":"HS256","typ":"JWT"}',encPayload:'{"sub":"123","name":"test","iat":1717084800,"exp":1748620800}',
      encAlg:'HS256',encSecret:'',encResult:'',encErr:'',
      algorithms:[
        {value:'HS256',label:'HS256 (HMAC-SHA256)'},
        {value:'HS384',label:'HS384 (HMAC-SHA384)'},
        {value:'HS512',label:'HS512 (HMAC-SHA512)'},
        {value:'RS256',label:'RS256 (RSA-SHA256)'},
        {value:'ES256',label:'ES256 (ECDSA-SHA256)'},
        {value:'none',label:'none (不签名)'}
      ]
    }
  },
  methods:{
    fmtJson:function(obj){return JSON.stringify(obj,null,2)},
    ts2str:function(ts){return new Date(ts*1000).toLocaleString(navigator.language||'en')},
    doDecode:function(){
      this.decoded=null;this.errorMsg='';this.sigResult=''
      if(!this.token.trim())return
      try{
        var parts=this.token.trim().split('.')
        if(parts.length!==3){this.errorMsg=this.$t('tool.JwtDecoder.errFormat');return}
        var header=JSON.parse(this._b64Decode(parts[0]))
        var payload=JSON.parse(this._b64Decode(parts[1]))
        this.decoded={header:header,payload:payload,signature:parts[2]}
      }catch(e){this.errorMsg=this.$t('tool.JwtDecoder.errParse')+': '+e.message}
    },
    _b64Decode:function(s){
      s=s.replace(/-/g,'+').replace(/_/g,'/')
      while(s.length%4)s+='='
      return decodeURIComponent(escape(atob(s)))
    },
    verifySig:function(){
      this.sigResult=''
      if(!this.decoded||!this.verifySecret)return
      var parts=this.token.trim().split('.')
      var data=parts[0]+'.'+parts[1]
      var alg=(this.decoded.header.alg||'HS256').toUpperCase()
      if(!alg.startsWith('HS')){this.sigResult='ok';return}
      var hash=alg==='HS256'?'SHA-256':alg==='HS384'?'SHA-384':'SHA-512'
      this._hmacVerify(data,this.verifySecret,hash,parts[2]).then(function(ok){
        this.sigResult=ok?'ok':'fail'
      }.bind(this)).catch(function(){this.sigResult='fail'}.bind(this))
    },
    _str2buf:function(s){return new TextEncoder().encode(s)},
    _buf2b64url:function(buf){return btoa(String.fromCharCode(...new Uint8Array(buf))).replace(/\+/g,'-').replace(/\//g,'_').replace(/=+$/,'')},
    _b64Url:function(s){return btoa(s).replace(/\+/g,'-').replace(/\//g,'_').replace(/=+$/,'')},
    _hmacSign:function(data,key,hash){
      var self=this
      return crypto.subtle.importKey('raw',self._str2buf(key),{name:'HMAC',hash:hash},false,['sign'])
        .then(function(k){return crypto.subtle.sign('HMAC',k,self._str2buf(data))})
        .then(function(sig){return self._buf2b64url(sig)})
    },
    _hmacVerify:function(data,key,hash,signature){
      var self=this
      return crypto.subtle.importKey('raw',self._str2buf(key),{name:'HMAC',hash:hash},false,['verify'])
        .then(function(k){
          var sigBytes=Uint8Array.from(atob(signature.replace(/-/g,'+').replace(/_/g,'/')),function(c){return c.charCodeAt(0)})
          return crypto.subtle.verify('HMAC',k,sigBytes,self._str2buf(data))
        })
    },
    doEncode:function(){
      this.encErr='';this.encResult=''
      var self=this
      try{
        var header=JSON.parse(this.encHeader.trim()||'{}')
        var payload=JSON.parse(this.encPayload.trim()||'{}')
        header.alg=this.encAlg
        var h=this._b64Url(JSON.stringify(header))
        var p=this._b64Url(JSON.stringify(payload))
        var token=h+'.'+p
        if(this.encAlg==='none'){
          this.encResult=token+'.'
        }else if(this.encSecret&&this.encAlg.startsWith('HS')){
          var hash=this.encAlg==='HS256'?'SHA-256':this.encAlg==='HS384'?'SHA-384':'SHA-512'
          this._hmacSign(token,this.encSecret,hash).then(function(sig){
            self.encResult=token+'.'+sig
          }).catch(function(e){self.encErr=self.$t('tool.JwtDecoder.errEncode')+': '+e.message})
        }else{
          token+='.'
          this.encResult=token
        }
      }catch(e){this.encErr=this.$t('tool.JwtDecoder.errEncode')+': '+e.message}
    },
    loadSample:function(){
      if(this.mode==='decode'){this.token=SAMPLE_TOKEN;this.doDecode()}
      else{this.encHeader='{"alg":"HS256","typ":"JWT"}';this.encPayload='{"sub":"123","name":"test","iat":1717084800,"exp":1748620800}';this.encResult='';this.encErr=''}
    },
    copy:function(t){navigator.clipboard.writeText(t);this.$message.success(this.$t("common.status.copied"))},
    doCopy:function(){
      var t=this.mode==='decode'?this.token:this.encResult
      if(!t){this.$message.warning(this.$t("common.status.noContent"));return}
      this.copy(t)
    },
    clearAll:function(){
      this.token='';this.decoded=null;this.errorMsg='';this.sigResult='';this.verifySecret=''
      this.encResult='';this.encErr=''
    },
    doAction:function(a){
      if(a==='copy')this.doCopy()
      if(a==='clear')this.clearAll()
    }
  }
}
</script>

<style scoped>
.fmt-tool{display:flex;flex-direction:column;gap:12px;max-width:100%}

.fmt-toolbar{display:flex;align-items:center;gap:8px;flex-wrap:wrap}
.fmt-toolbar >>> .el-button + .el-button{margin-left:0}
.fmt-label{font-size:13px;font-weight:700;color:#1a1f36}
.fmt-spacer{flex:1}

.uc-chip{padding:5px 14px;border-radius:6px;font-size:12px;font-weight:600;cursor:pointer;color:#909399;background:#f0f2f5;transition:.12s;border:1px solid transparent}
.uc-chip:hover{color:#4e7cff}
.uc-chip.on{background:#e8f0fe;color:#4e7cff;border-color:#4e7cff}

.fmt-textarea >>> textarea{
  font-family:'JetBrains Mono','Fira Code','Consolas',monospace!important;
  font-size:13px!important;line-height:1.6!important;
  border-radius:10px;resize:vertical;min-height:240px
}
.jw-short >>> textarea{min-height:240px!important}

.fmt-status-bar{font-size:12px}
.fmt-err{color:#f56c6c;display:flex;align-items:center;gap:4px}
.fmt-ok{color:#67c23a;display:flex;align-items:center;gap:4px}
.fmt-hint{color:#c0c4cc}

/* 解码面板 */
.jw-panes{display:flex;gap:14px}
.jw-pane{flex:1;min-width:0;background:#fafbfc;border:1px solid #ebeef5;border-radius:10px;padding:14px 16px}
.jw-pane-title{font-size:12px;font-weight:700;color:#909399;margin-bottom:8px}
.jw-sig-hint{font-weight:400;color:#c0c4cc}
.jw-code{font-size:12px;font-family:monospace;color:#303133;line-height:1.5;white-space:pre-wrap;word-break:break-all;margin:0;background:#fff;padding:10px;border-radius:6px;max-height:200px;overflow:auto}
.jw-claims{display:flex;flex-direction:column;gap:4px;margin-top:10px}
.jw-claim{display:flex;align-items:center;gap:8px;font-size:12px;padding:4px 0}
.jw-claim span{color:#8892a6;width:56px;flex-shrink:0}
.jw-claim code{font-family:monospace;color:#303133}
.jw-sig-pane{margin-top:0}
.jw-sig{font-size:11px;font-family:monospace;color:#8892a6;word-break:break-all}
.jw-badge{font-size:10px;padding:2px 8px;border-radius:10px;font-weight:600}
.jw-badge-ok{background:#e8f8e8;color:#43a047}
.jw-badge-err{background:#fef0f0;color:#e74c3c}

.jw-verify{display:flex;align-items:center;gap:8px;margin-top:4px}
.jw-mono >>> input{font-family:monospace!important}

/* 编码控制 */
.jw-encode-ctrl{display:flex;align-items:center;gap:8px;flex-wrap:wrap}
.jw-result{background:#f0f9eb;border:1px solid #e1f3d8;border-radius:10px;padding:14px 16px;margin-top:4px}
.jw-result-token{display:block;font-size:12px;font-family:monospace;color:#303133;word-break:break-all;line-height:1.6;margin-bottom:8px;max-height:200px;overflow-y:auto}

@media(max-width:768px){.jw-panes{flex-direction:column}}
</style>
